import React from "react";
import { Box, Table, Title } from "@mantine/core";

import { useTranslation } from "react-i18next";
import PercentageIndicator from "../organization_dashboard/PageIndicatorComponent";
import { TestCountAnalytics, TimePeriod } from "types/analyticsTypes";

interface TestCountViewProps {
  analytics: TestCountAnalytics;
  mainTitle?: { title?: string; display: boolean };
  displayedPeriods?: TimePeriod[];
  displayPeriodTitle?: boolean;
  style?: React.CSSProperties;
  useColorToDisplayCount?: boolean;
}

/**
 * TestCountView Component
 *
 * This component is a reusable table-based view for displaying test count analytics
 * over different time periods (week, month, quarter, year). It uses the `PercentageIndicator`
 * component to show the count, percentage change, and previous count for each period.
 * The component is highly customizable through its props.
 *
 * @param {TestCountViewProps} props - The props for the TestCountView component.
 * @param {TestCountAnalytics} props.analytics - The analytics data containing test counts,
 * percentage changes, and previous counts for different time periods.
 * @param {{title: string, display: boolean}} [props.mainTitle] - An optional main title object containing
 * the title text and a boolean to determine whether to display it.
 * @param {TimePeriod[]} [props.displayedPeriods=[TimePeriod.MONTH, TimePeriod.QUARTER, TimePeriod.WEEK, TimePeriod.YEAR]] -
 * An array of time periods to display in the table. Defaults to showing all supported periods.
 * @param {boolean} [props.displayPeriodTitle=true] - Whether to display the title for each time period.
 * Defaults to `true`.
 * @param {string} [props.title] - An optional title for the component.
 * @param {React.CSSProperties} [props.style] - Optional custom styles to apply to the table.
 * @param {boolean} [props.useColorToDisplayCount=false] - Whether to use color coding for the test count
 * values. Defaults to `false`.

 *
 * @returns {React.FC<TestCountViewProps>} A table displaying test count analytics for the specified time periods.
 */

const TestCountView: React.FC<TestCountViewProps> = ({
  analytics,
  mainTitle = { title: "analytics.common.testCounts.title", display: true },
  displayedPeriods = [TimePeriod.MONTH, TimePeriod.QUARTER, TimePeriod.WEEK, TimePeriod.YEAR],
  displayPeriodTitle = true,
  useColorToDisplayCount = false,
  style,
}) => {
  const { t } = useTranslation();

  return (
    <Box w={"100%"} p={"md"}>
      {mainTitle.display && <Title order={3}>{t(mainTitle.title)}</Title>}
      <Table style={[style]}>
        <Table.Tbody>
          {displayedPeriods.includes(TimePeriod.WEEK) && (
            <Table.Tr>
              {displayPeriodTitle && (
                <Table.Td>{t("analytics.common.testCounts.last7DaysDescription")}</Table.Td>
              )}
              <Table.Td>
                {analytics?.week && (
                  <PercentageIndicator
                    count={analytics.week.count}
                    percentageChange={analytics.week.percentageChange}
                    previousCount={analytics.week.previousCount}
                    useColorToDisplayCount={useColorToDisplayCount}
                  />
                )}
              </Table.Td>
            </Table.Tr>
          )}
          {displayedPeriods.includes(TimePeriod.MONTH) && (
            <Table.Tr>
              {displayPeriodTitle && (
                <Table.Td>{t("analytics.common.testCounts.last30DaysDescription")}</Table.Td>
              )}
              <Table.Td>
                {analytics?.month && (
                  <PercentageIndicator
                    count={analytics.month.count}
                    percentageChange={analytics.month.percentageChange}
                    previousCount={analytics.month.previousCount}
                    useColorToDisplayCount={useColorToDisplayCount}
                  />
                )}
              </Table.Td>
            </Table.Tr>
          )}
          {displayedPeriods.includes(TimePeriod.QUARTER) && (
            <Table.Tr>
              {displayPeriodTitle && (
                <Table.Td>{t("analytics.common.testCounts.last90DaysDescription")}</Table.Td>
              )}
              <Table.Td>
                {analytics?.quarter && (
                  <PercentageIndicator
                    count={analytics.quarter.count}
                    percentageChange={analytics.quarter.percentageChange}
                    previousCount={analytics.quarter.previousCount}
                    useColorToDisplayCount={useColorToDisplayCount}
                  />
                )}
              </Table.Td>
            </Table.Tr>
          )}
          {displayedPeriods.includes(TimePeriod.YEAR) && (
            <Table.Tr>
              {displayPeriodTitle && (
                <Table.Td>{t("analytics.common.testCounts.last365DaysDescription")}</Table.Td>
              )}
              <Table.Td>
                {analytics?.year && (
                  <PercentageIndicator
                    count={analytics.year.count}
                    percentageChange={analytics.year.percentageChange}
                    previousCount={analytics.year.previousCount}
                    useColorToDisplayCount={useColorToDisplayCount}
                  />
                )}
              </Table.Td>
            </Table.Tr>
          )}
          {displayedPeriods.includes(TimePeriod.RANGE) && (
            <Table.Tr>
              {displayPeriodTitle && (
                <Table.Td>{t("analytics.common.testCounts.selectedRange")}</Table.Td>
              )}
              <Table.Td>
                {analytics?.range && (
                  <PercentageIndicator
                    count={analytics.range.count}
                    percentageChange={analytics.range.percentageChange}
                    previousCount={analytics.range.previousCount}
                    useColorToDisplayCount={useColorToDisplayCount}
                  />
                )}
              </Table.Td>
            </Table.Tr>
          )}
        </Table.Tbody>
      </Table>
    </Box>
  );
};

export default TestCountView;
